<template>
  <div class="stepper">
    <template v-for="(step, index) in steps" :key="index">
      <div
        :class="[
          'stepper__step',
          {
            'stepper__step--completed': index < currentStep,
            'stepper__step--active': index === currentStep,
            'stepper__step--pending': index > currentStep,
            'stepper__step--clickable': isStepClickable(index),
          },
        ]"
        @click="handleStepClick(index)"
      >
        <div class="stepper__circle">
          <Icon v-if="index < currentStep" icon="solar:check-read-linear" width="14" height="14" />
          <span v-else class="stepper__number">{{ index + 1 }}</span>
        </div>
        <div class="stepper__label">{{ step }}</div>
      </div>
      <div
        v-if="index < steps.length - 1"
        :class="['stepper__line', { 'stepper__line--completed': index < currentStep }]"
      ></div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { Icon } from "@iconify/vue";

interface Props {
  steps: string[];
  currentStep: number;
  // Opt-in — steppers elsewhere in the app stay purely visual unless a
  // caller explicitly asks for click-to-navigate.
  clickable?: boolean;
  // Highest step index the caller has already let the user reach. A step
  // beyond this stays inert even when clickable, so this only ever lets
  // someone jump to an already-visited step, never skip ahead into one they
  // haven't gotten to yet.
  maxStep?: number;
}

const props = withDefaults(defineProps<Props>(), {
  steps: () => [],
  currentStep: 0,
  clickable: false,
  maxStep: 0,
});

const emit = defineEmits<{ "step-click": [index: number] }>();

const isStepClickable = (index: number) =>
  props.clickable &&
  index !== props.currentStep &&
  index <= Math.max(props.maxStep, props.currentStep);

const handleStepClick = (index: number) => {
  if (isStepClickable(index)) emit("step-click", index);
};
</script>

<style scoped>
.stepper {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  margin-bottom: 48px;
  padding: 0 20px;
}

.stepper__step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  position: relative;
}

.stepper__circle {
  width: 36px;
  height: 36px;
  border: 2px solid #e0e0e0;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #9ca3af;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.stepper__number {
  font-weight: 600;
}

.stepper__step--completed .stepper__circle {
  background: var(--color-primary, #3e3efe);
  border-color: transparent;
  color: white;
  box-shadow: none;
}

.stepper__step--active .stepper__circle {
  border-color: var(--color-primary, #3e3efe);
  border-width: 2px;
  color: var(--color-primary, #3e3efe);
  background: var(--color-primary-light, #f8f7ff);
  box-shadow: none;
}

.stepper__step--pending .stepper__circle {
  border-color: #d1d5db;
  color: #9ca3af;
}

.stepper__step--clickable {
  cursor: pointer;
}

.stepper__step--clickable:hover .stepper__circle {
  border-color: var(--color-primary, #3e3efe);
}

.stepper__step--clickable:hover .stepper__label {
  color: var(--color-primary, #3e3efe);
}

.stepper__label {
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  margin-top: 10px;
  color: #6b7280;
  transition: color 0.3s ease;
}

.stepper__step--completed .stepper__label {
  color: var(--color-primary, #3e3efe);
  font-weight: 600;
}

.stepper__step--active .stepper__label {
  color: var(--color-primary, #3e3efe);
  font-weight: 600;
}

.stepper__step--pending .stepper__label {
  color: #9ca3af;
}

.stepper__line {
  height: 2px;
  background: #e5e7eb;
  flex-grow: 1;
  margin: 17px 12px 0 12px;
  border-radius: 1px;
  transition: background 0.3s ease;
}

.stepper__line--completed {
  background: var(--color-primary, #3e3efe);
}
</style>
